import { useEffect } from "react";
import {
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import {
  exhibitorAvailableLanguagesStore,
  useExhibitorAvailableLanguagesStore,
} from "store/localization/exhibitor-available-languages.store";
import type { ShowLanguage } from "view/components/SettingsRows/ShowLanguagesRow/useShowLanguages";
import { usePublishedLanguages } from "./usePublishedLanguages";

const LISTENER_ID = "effective_show_languages";

const REFETCH_ON: NotificationType[] = [
  NotificationType.UPDATED_SHOW,
  NotificationType.PAID_FEATURES_CREATED,
];

/*
 * Mounted instances per Show. The gateway keeps one callback per listener id,
 * so the subscription is shared and dropped only with the last instance —
 * unsubscribing per instance left every other instance without refetches.
 */
const instancesByShowId = new Map<string, number>();

const subscribeToRefetch = (showId: string) => {
  const count = instancesByShowId.get(showId) ?? 0;

  instancesByShowId.set(showId, count + 1);

  if (count > 0) return;

  REFETCH_ON.forEach((key) => {
    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key,
      showId,
      listenerId: LISTENER_ID,
      callback: async (message) => {
        if (message.showId === showId) {
          await exhibitorAvailableLanguagesStore
            .getState()
            .fetchEffective(showId, true);
        }
      },
    });
  });
};

const unsubscribeFromRefetch = (showId: string) => {
  const count = (instancesByShowId.get(showId) ?? 1) - 1;

  if (count > 0) {
    instancesByShowId.set(showId, count);
    return;
  }

  instancesByShowId.delete(showId);

  REFETCH_ON.forEach((key) =>
    apiV2.notification.unsubscribe({
      key,
      showId,
      listenerId: LISTENER_ID,
    })
  );
};

export const useEffectiveShowLanguages = (showId?: string) => {
  const {
    languages: published,
    isLoading: isPublishedLoading,
  } = usePublishedLanguages();

  const {
    entry,
    isLoading,
    fetchEffective,
  } = useExhibitorAvailableLanguagesStore((state) => ({
    entry: showId ? state.effectiveByShowId[showId] : undefined,
    isLoading: state.effectiveLoadingShowId === showId,
    fetchEffective: state.fetchEffective,
  }));

  useEffect(() => {
    if (showId) {
      fetchEffective(showId);
    }
  }, [showId, fetchEffective]);

  useEffect(() => {
    if (!showId) return undefined;

    subscribeToRefetch(showId);

    return () => unsubscribeFromRefetch(showId);
  }, [showId]);

  const effectiveCodes = new Set(entry?.languageCodes ?? []);
  const effective: ShowLanguage[] = published.filter((language) =>
    effectiveCodes.has(language.code)
  );

  const translatableCodes = new Set(entry?.translatableLanguageCodes ?? []);
  const translatable: ShowLanguage[] = published.filter((language) =>
    translatableCodes.has(language.code)
  );

  return {
    effective,
    effectiveCodes: entry?.languageCodes ?? [],
    translatable,
    translatableCodes: entry?.translatableLanguageCodes ?? [],
    defaultLanguage: entry?.defaultLanguage,
    adminDisabledCodes: entry?.adminDisabledLanguageCodes ?? [],
    isLoading: isLoading || isPublishedLoading,
  };
};
